import { convertLanguage } from '@/core/i18n/convertLanguage'

/**
 * Auth / Docs 画面（バックエンドが返す公開画面）が表示できるロケール。
 *
 * ⚠️ バックエンドの `LocaleResolver::PAGE_LOCALES` と一致させること（#3487）。
 *    backend/src/src/Service/v2/LocaleResolver.php
 *    バックエンドは翻訳カタログ（backend/src/translations/messages.*.yaml）のある言語しか
 *    表示できず、このリストに無い値を渡すと ja_JP に丸められる。
 *    バックエンドがカタログを追加・削除したらこの定数も更新すること。
 *
 * en_SG（#3601）・de_DE / es_ES / it_IT（#3614）はバックエンドにカタログが追加されたため、
 * en_US に寄せずにそのまま渡す（#3641）。並び順はバックエンドと同じにしている。
 */
export const PUBLIC_PAGE_LOCALES = [
  'ja_JP',
  'en_US',
  'en_SG',
  'ko_KR',
  'zh_CH',
  'zh_TW',
  'fr_FR',
  'de_DE',
  'es_ES',
  'it_IT',
] as const
export type PublicPageLocale = (typeof PUBLIC_PAGE_LOCALES)[number]

/** バックエンドの `LocaleResolver::DEFAULT_LOCALE`。この値のときは `lang` を付けない。 */
export const PUBLIC_PAGE_DEFAULT_LOCALE: PublicPageLocale = 'ja_JP'

/** バックエンドの `LocaleResolver::LOCALE_QUERY_PARAM`。 */
const LOCALE_QUERY_PARAM = 'lang'

/**
 * SPA のロケールを公開画面が表示できるロケールに寄せる。
 *
 * 現在は SPA の対応言語（`SUPPORTED_LOCALES`）がすべて公開画面にもある。
 * 一覧に無い値（SPA に言語を追加したがバックエンドのカタログがまだ無い場合等）をそのまま渡すと
 * バックエンドが ja_JP に丸めるため、日本語よりは読める可能性が高い en_US にフォールバックする。
 */
export const toPublicPageLocale = (locale: string | null | undefined): PublicPageLocale => {
  if (!locale) return PUBLIC_PAGE_DEFAULT_LOCALE

  return (PUBLIC_PAGE_LOCALES as readonly string[]).includes(locale) ? (locale as PublicPageLocale) : 'en_US'
}

/**
 * 現在のロケールを決定する。API の `X-Local` ヘッダーに載せる値と同じものを返すこと。
 *
 * @param i18nKey `useCampaignSlug().LOCAL_STORAGE_I18N_KEY`（= `{campaignSlug}_i18nextLng`）
 */
export const resolveLocale = (i18nKey: string): string => {
  // SSR / static export の生成時は window が無いため既定値に倒す。
  // ハイドレーション後に localStorage の値で解決し直される。
  if (typeof window === 'undefined') return PUBLIC_PAGE_DEFAULT_LOCALE

  try {
    const stored = localStorage.getItem(i18nKey)
    if (stored) return stored
  } catch {
    // localStorage が使用不可（Safari プライベートモード等）。ブラウザの検出値にフォールバックする
  }

  return convertLanguage(navigator.language)
}

/**
 * Auth / Docs 画面への URL に `lang` クエリを付与する。
 *
 * - 既定ロケール（ja_JP）のときは付けない。URL を汚さず、現行の URL と完全に一致させるため
 * - `http(s)` 以外のスキーム（`mailto:` 等）は素通しする
 * - 既存のクエリ文字列は文字列のまま温存する。`URL` / `URLSearchParams` で往復させると
 *   `return_to` のパーセントエンコーディングが再構成され、値が変わり得るため
 * - フラグメントを切り出してから付与する。単純に連結すると `...#top?lang=en_US` となり
 *   クエリがサーバーに届かない（バックエンドの `locale_link()` と同じ扱い）
 */
export const withLangParam = (url: string, locale: string | null | undefined): string => {
  if (!/^https?:\/\//i.test(url)) return url

  const lang = toPublicPageLocale(locale)
  if (lang === PUBLIC_PAGE_DEFAULT_LOCALE) return url

  const hashIndex = url.indexOf('#')
  const base = hashIndex === -1 ? url : url.slice(0, hashIndex)
  const hash = hashIndex === -1 ? '' : url.slice(hashIndex)
  const separator = base.includes('?') ? '&' : '?'

  return `${base}${separator}${LOCALE_QUERY_PARAM}=${lang}${hash}`
}
